'use client';

import { c as _c } from "react/compiler-runtime";
import { ArrowLeftIcon, HomeIcon, BeakerIcon, CodeBracketIcon, RectangleStackIcon, EnvelopeIcon, SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import { m, AnimatePresence } from 'motion/react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useTheme } from 'next-themes';
import { useState } from 'react';
import { Dock, DockItem, DockIcon, DockLabel } from '@/components/ui/dock';
import { AnimatedThemeToggler } from '@/components/magicui/animated-theme-toggler';
import { useWebHaptics } from 'web-haptics/react';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useIsClientMounted } from '@/hooks/useIsClientMounted';
import { GitHubIcon, XIcon } from '@/components/SocialIcons';
import { CONTACT_EMAIL, SOCIAL_URLS } from '@/lib/contact';
import { PRIMARY_NAV_BY_HREF } from '@/lib/nav';

// The dock shows a reordered subset of the primary nav (no About), each paired
// with an icon + aria-label. Hrefs/labels come from the shared nav config.
type DockNavLink = {
  href: string;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  ariaLabel: string;
};
const DOCK_NAV: readonly DockNavLink[] = [{
  ...PRIMARY_NAV_BY_HREF['/craft']!,
  icon: BeakerIcon,
  ariaLabel: 'Go to craft'
}, {
  ...PRIMARY_NAV_BY_HREF['/projects']!,
  icon: CodeBracketIcon,
  ariaLabel: 'Go to projects'
}, {
  ...PRIMARY_NAV_BY_HREF['/stack']!,
  icon: RectangleStackIcon,
  ariaLabel: 'Go to stack'
}];
function AppDock() {
  const $ = _c(40);
  const {
    back
  } = useRouter();
  const {
    resolvedTheme
  } = useTheme();
  const mounted = useIsClientMounted();
  const [showToast, setShowToast] = useState(false);
  const haptic = useWebHaptics();
  const isMobile = useIsMobile();
  let t0;
  if ($[0] !== haptic) {
    t0 = () => {
      haptic.trigger("success");
      navigator.clipboard.writeText(CONTACT_EMAIL);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
    };
    $[0] = haptic;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const copyEmailToClipboard = t0;
  if (!mounted) {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="fixed right-0 bottom-8 left-0 z-[9999]"><div className="mx-auto flex w-fit gap-4 rounded-full bg-neutral-100/90 px-4 dark:bg-neutral-900/90" style={{
          height: 64
        }} /></div>;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== showToast) {
    t1 = showToast && <m.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} transition={{
      duration: 0.2
    }} className="fixed top-4 left-1/2 z-[9999] -translate-x-1/2 transform rounded-md bg-neutral-200 px-4 py-2 text-neutral-800 shadow-lg dark:bg-neutral-800 dark:text-white">Email copied to clipboard!</m.div>;
    $[3] = showToast;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <AnimatePresence>{t1}</AnimatePresence>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DockIcon><ArrowLeftIcon className="size-full text-neutral-600 dark:text-neutral-400" /></DockIcon>;
    t4 = <DockLabel>Back</DockLabel>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[9] !== back || $[10] !== haptic) {
    t5 = <DockItem className="aspect-square rounded-full bg-neutral-200 dark:bg-neutral-800">{t3}{t4}<button type="button" className="absolute inset-0" onClick={() => {
        haptic.trigger("light");
        back();
      }} aria-label="Go back" /></DockItem>;
    $[9] = back;
    $[10] = haptic;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <DockIcon><HomeIcon className="size-full text-neutral-600 dark:text-neutral-400" /></DockIcon>;
    t7 = <DockLabel>Home</DockLabel>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  let t8;
  let t9;
  if ($[14] !== haptic) {
    t8 = <DockItem className="aspect-square rounded-full bg-neutral-200 dark:bg-neutral-800">{t6}{t7}<Link href="/" className="absolute inset-0" onClick={() => haptic.trigger("light")} aria-label="Go to home" /></DockItem>;
    t9 = DOCK_NAV.map(t10 => {
      const {
        href,
        label,
        icon: Icon,
        ariaLabel
      } = t10;
      return <DockItem key={href} className="aspect-square rounded-full bg-neutral-200 dark:bg-neutral-800"><DockIcon><Icon className="size-full text-neutral-600 dark:text-neutral-400" /></DockIcon><DockLabel>{label}</DockLabel><Link href={href} className="absolute inset-0" onClick={() => haptic.trigger("light")} aria-label={ariaLabel} /></DockItem>;
    });
    $[14] = haptic;
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  let t10;
  if ($[17] !== isMobile) {
    t10 = !isMobile && <div className="mx-1 h-8 w-px self-center bg-neutral-400 dark:bg-neutral-700" />;
    $[17] = isMobile;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== copyEmailToClipboard || $[20] !== isMobile) {
    t11 = !isMobile && <><DockItem className="aspect-square rounded-full bg-neutral-200 dark:bg-neutral-800"><DockIcon><div className="flex size-full items-center justify-center"><XIcon className="size-5 text-neutral-600 dark:text-neutral-400" /></div></DockIcon><DockLabel>X</DockLabel><a className="absolute inset-0" href={SOCIAL_URLS.twitter} target="_blank" rel="noopener noreferrer" aria-label="Open X/Twitter" /></DockItem><DockItem className="aspect-square rounded-full bg-neutral-200 dark:bg-neutral-800"><DockIcon><div className="flex size-full items-center justify-center"><GitHubIcon className="size-5 text-neutral-600 dark:text-neutral-400" /></div></DockIcon><DockLabel>GitHub</DockLabel><a className="absolute inset-0" href={SOCIAL_URLS.github} target="_blank" rel="noopener noreferrer" aria-label="Open GitHub" /></DockItem><DockItem className="aspect-square rounded-full bg-neutral-200 dark:bg-neutral-800"><DockIcon><EnvelopeIcon className="size-full text-neutral-600 dark:text-neutral-400" /></DockIcon><DockLabel>Email</DockLabel><button type="button" className="absolute inset-0" onClick={copyEmailToClipboard} aria-label="Copy email to clipboard" /></DockItem></>;
    $[19] = copyEmailToClipboard;
    $[20] = isMobile;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== resolvedTheme) {
    t12 = <DockIcon>{resolvedTheme === "dark" ? <SunIcon className="size-full text-neutral-600 dark:text-neutral-400" /> : <MoonIcon className="size-full text-neutral-600 dark:text-neutral-400" />}</DockIcon>;
    $[22] = resolvedTheme;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = resolvedTheme === "dark" ? "Light Mode" : "Dark Mode";
  let t14;
  if ($[24] !== t13) {
    t14 = <DockLabel>{t13}</DockLabel>;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <AnimatedThemeToggler className="absolute inset-0" />;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t12 || $[28] !== t14) {
    t16 = <DockItem className="aspect-square rounded-full bg-neutral-200 dark:bg-neutral-800">{t12}{t14}{t15}</DockItem>;
    $[27] = t12;
    $[28] = t14;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t10 || $[31] !== t11 || $[32] !== t16 || $[33] !== t5 || $[34] !== t8 || $[35] !== t9) {
    t17 = <div className="fixed bottom-8 left-1/2 z-[9999] max-w-full -translate-x-1/2"><Dock className="items-center overflow-visible rounded-full bg-neutral-100/90 px-4 py-2 shadow-lg backdrop-blur-md dark:bg-neutral-900/90">{t5}{t8}{t9}{t10}{t11}{t16}</Dock></div>;
    $[30] = t10;
    $[31] = t11;
    $[32] = t16;
    $[33] = t5;
    $[34] = t8;
    $[35] = t9;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t17 || $[38] !== t2) {
    t18 = <>{t2}{t17}</>;
    $[37] = t17;
    $[38] = t2;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  return t18;
}
export default AppDock;